import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Alert, Grid, LinearProgress } from "@mui/material";

import { imageURL } from "../Data/baseURL";
import FileHeader from "./FileHeader";
import axiosInstance from "../../services/axios/axios";

const SingleFileUploadWithProgress = React.memo(
  ({ file, onDelete, onUpload }) => {
    const location = useLocation();
    const { t } = useTranslation();
    const [progress, setProgress] = useState(0);
    const [uploadError, setUploadError] = useState("");
    const uploadTask = useRef(null);
    const handlers = useRef({ onUpload, t });

    useEffect(() => {
      handlers.current = { onUpload, t };
    }, [onUpload, t]);

    useEffect(() => {
      let active = true;
      setUploadError("");

      // Reuse the same request during React Strict Mode's effect replay.
      if (!uploadTask.current || uploadTask.current.file !== file) {
        uploadTask.current = {
          file,
          promise: uploadFile(file, setProgress),
        };
      }

      const upload = async () => {
        try {
          const response = await uploadTask.current.promise;

          if (!active) {
            return;
          }

          const result = await handlers.current.onUpload(file, response);

          if (active && result === false) {
            setUploadError(handlers.current.t("toast.not_saved_try_again"));
          }
        } catch (error) {
          if (active) {
            const detail = error?.response?.data?.detail;
            const fileError = error?.response?.data?.file_obj?.[0];

            setUploadError(
              (typeof detail === "string" && detail) ||
                (typeof fileError === "string" && fileError) ||
                handlers.current.t("toast.not_saved_try_again")
            );
          }
        }
      };

      void upload();

      return () => {
        active = false;
      };
    }, [file]);

    return (
      <Grid item xs={12}>
        {location.pathname !== "/apps/account" && (
          <>
            <FileHeader file={file} onDelete={onDelete} />
            <LinearProgress variant="determinate" value={progress} />
          </>
        )}
        {uploadError && <Alert severity="error">{uploadError}</Alert>}
      </Grid>
    );
  }
);

export default SingleFileUploadWithProgress;

async function uploadFile(file, onProgress) {
  const formData = new FormData();
  formData.append("file_obj", file, file.name);
  formData.append("file_name", file.name);

  const response = await axiosInstance({
    url: imageURL,
    method: "POST",
    data: formData,
    timeout: 120000,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (event) => {
      if (event.total > 0) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });

  return response.data;
}
